import { useState } from 'react';

import { api } from '../api/client';
import type { DashboardTile, SavedChart, SavedDashboard } from '../types';
import { ChartRenderer } from './ChartRenderer';

/**
 * Assemble saved charts into a dashboard. The slug is what the platform stores
 * in the content item's `dashboardUrl` (`/d/<slug>`), so cutting a report over
 * is a one-field edit on the platform side — no deploy.
 */
interface Props {
  charts: SavedChart[];
  editing?: SavedDashboard | null;
  onSaved: (dashboard: SavedDashboard) => void;
  onCancel: () => void;
}

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const DashboardBuilder = ({ charts, editing, onSaved, onCancel }: Props) => {
  const [name, setName] = useState(editing?.name ?? '');
  const [slug, setSlug] = useState(editing?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(editing));
  const [description, setDescription] = useState(editing?.description ?? '');
  const [appFilter, setAppFilter] = useState<SavedDashboard['appFilter']>(
    editing?.appFilter ?? 'none',
  );
  const [tiles, setTiles] = useState<DashboardTile[]>(editing?.tiles ?? []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const updateName = (value: string) => {
    setName(value);
    // Auto-derive the slug until the user edits it themselves.
    if (!slugTouched) setSlug(slugify(value));
  };

  const addTile = (chartId: string) =>
    setTiles((current) => [...current, { chartId, width: 6, height: 4 }]);

  const updateTile = (index: number, patch: Partial<DashboardTile>) =>
    setTiles((current) => current.map((t, i) => (i === index ? { ...t, ...patch } : t)));

  const removeTile = (index: number) =>
    setTiles((current) => current.filter((_, i) => i !== index));

  const moveTile = (index: number, delta: number) =>
    setTiles((current) => {
      const target = index + delta;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const save = async () => {
    if (!name.trim() || !slug.trim()) return;
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await api.saveDashboard({
          id: editing?.id,
          name: name.trim(),
          slug: slug.trim(),
          description: description.trim() || undefined,
          appFilter,
          tiles,
        }),
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const chartById = (id: string) => charts.find((c) => c.id === id);

  return (
    <div className="studio">
      <div className="studio__panel">
        <h2>{editing ? 'Edit dashboard' : 'New dashboard'}</h2>

        <label>
          <span>Name</span>
          <input value={name} onChange={(e) => updateName(e.target.value)} placeholder="App performance" />
        </label>

        <label>
          <span>Slug</span>
          <input
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugify(e.target.value));
            }}
            placeholder="app-performance"
          />
        </label>
        <p className="hint">
          The platform points a content item at <code>/d/{slug || 'your-slug'}</code>.
        </p>

        <label>
          <span>Description</span>
          <input value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <label>
          <span>App filter</span>
          <select
            value={appFilter}
            onChange={(e) => setAppFilter(e.target.value as SavedDashboard['appFilter'])}
          >
            <option value="none">None — same data for everyone</option>
            <option value="single">Single app (app_id)</option>
            <option value="multiple">Multiple apps (app_ids)</option>
          </select>
        </label>

        <h3>Add a chart</h3>
        <div className="chart-picker">
          {charts.length === 0 && <p className="hint">Create a chart first.</p>}
          {charts.map((c) => (
            <button key={c.id} onClick={() => addTile(c.id)}>
              + {c.name}
            </button>
          ))}
        </div>

        <h3>Layout</h3>
        {tiles.length === 0 && <p className="hint">No tiles yet.</p>}
        <ol className="tile-list">
          {tiles.map((tile, i) => {
            const chart = chartById(tile.chartId);
            return (
              <li key={`${tile.chartId}-${i}`}>
                <span className="tile-list__name">{chart?.name ?? 'Missing chart'}</span>
                <label>
                  w
                  <input
                    type="number"
                    min={1}
                    max={12}
                    value={tile.width}
                    onChange={(e) => updateTile(i, { width: Number(e.target.value) })}
                  />
                </label>
                <label>
                  h
                  <input
                    type="number"
                    min={1}
                    value={tile.height}
                    onChange={(e) => updateTile(i, { height: Number(e.target.value) })}
                  />
                </label>
                <button onClick={() => moveTile(i, -1)} aria-label="Move up">↑</button>
                <button onClick={() => moveTile(i, 1)} aria-label="Move down">↓</button>
                <button onClick={() => removeTile(i)} aria-label="Remove">✕</button>
              </li>
            );
          })}
        </ol>

        {error && <p className="error">{error}</p>}

        <div className="studio__actions">
          <button onClick={onCancel}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy || !name.trim() || !slug.trim()}>
            {editing ? 'Save changes' : 'Create dashboard'}
          </button>
        </div>
      </div>

      <div className="studio__preview">
        <header>Preview</header>
        <div className="dashboard-grid">
          {tiles.map((tile, i) => {
            const chart = chartById(tile.chartId);
            if (!chart) return null;
            return (
              <div
                className="dashboard-tile"
                key={`${tile.chartId}-${i}`}
                style={{ gridColumn: `span ${tile.width}`, minHeight: tile.height * 56 }}
              >
                <h4>{tile.title ?? chart.name}</h4>
                <ChartRenderer chart={chart} />
              </div>
            );
          })}
          {tiles.length === 0 && <div className="chart-empty">Add a chart to see the layout</div>}
        </div>
      </div>
    </div>
  );
};
